import { Button, Layout } from 'antd';
import './index.scss';
import Sidebar from './Sidebar/Sidebar';
import { ReceptionistDashboard } from '../ReceptionistDashboard/ReceptionistDashboard';
import { useEffect, useMemo, useRef, useState } from 'react';
import ContentComponent from './ContentComponent/ContentComponent';
import { getLoggedInUser, getcurrentPage, getSelectedAgentId } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { useQuery } from '@tanstack/react-query';
import { setCurrentPage, setSelectedAgent, setSelectedAgentId } from '@/store/account/slice';
import { useDispatch } from 'react-redux';
import posthog from 'posthog-js';
import { setReseller } from '@/store/reseller/slice';
import { useSearchParams } from 'react-router-dom';
import { getReceptionistReseller, getResellerLogo } from '@/api/unbrandedReceptionist';
import { MenuOutlined } from '@mui/icons-material';
import { Theme } from '@/interfaces/IThemeInterface';
import { setAppTheme } from '@/store/app/slice';
import { getResellerData } from '@/store/reseller/selector';
import MobileLogo from '@/assets/images/mobile-log.png';
import DesktopLogo from '@/assets/images/desktop-logo.png';
import { useMobileDeviceCheck } from 'src/common/hooks/useMobile';
import { UserDashboardSiderItem } from 'src/enums/EUserDashboardSiderItem';
import ReceptionistSubCancelPromo from '../ReceptionistDashboard/AgentSubCancelPromo';
import { useAgents } from 'src/Hooks/useAgents';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import { Loading } from '@/components/Loading';
import LocalStorageUtility from '@/utils/localStorage';
import { ErrorOutline, Refresh, Logout } from '@mui/icons-material';

const { Content } = Layout;

export const UserDashboard: React.FC<{ debrand: boolean }> = ({ debrand }) => {
	const { isMobile } = useMobileDeviceCheck();
	const [searchParams] = useSearchParams();
	const dispatch = useDispatch();
	const loggedInUser = useAppSelector(getLoggedInUser);
	const currentPage = useAppSelector(getcurrentPage);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const currentReseller = useAppSelector(getResellerData);
	const { inboundAgents, outboundAgents, refetchAgents } = useAgents();
	const [isInitializing, setIsInitializing] = useState(true);
	const [initializationError, setInitializationError] = useState<string | null>(null);
	const [shouldShowReceptionistDashboard, setShouldShowReceptionistDashboard] = useState(false);
	const hasInitialized = useRef(false);
	const hasAttemptedInitialization = useRef(false);

	const menuRef = useRef<HTMLDivElement>(null);
	const [drawerVisible, setDrawerVisible] = useState(false);

	const openDrawer = () => setDrawerVisible(true);
	const closeDrawer = () => setDrawerVisible(false);
	const [isSmallScreen, setIsSmallScreen] = useState(window.outerWidth < 768);

	const handleBackdropClick = () => {
		closeDrawer();
	};

	const handleSidebarClick = (e: React.MouseEvent) => {
		e.stopPropagation();
	};

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && drawerVisible) {
				closeDrawer();
			}
		};

		if (drawerVisible) {
			document.addEventListener('keydown', handleKeyDown);
			// Prevent body scroll when drawer is open
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'unset';
		}

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = 'unset';
		};
	}, [drawerVisible]);

	const { data: logoData } = useQuery({
		queryKey: ['reseller-logo'],
		queryFn: () => getResellerLogo({ key: currentReseller.logo as string }),
		enabled: !!(debrand && currentReseller?.logo),
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const resellerLogoUrl = useMemo(() => {
		if (!logoData) {
			return;
		}
		return URL.createObjectURL(logoData);
	}, [logoData]);

	const logo = (
		<div className={''}>
			{debrand ? (
				resellerLogoUrl ? (
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<img height={80} width={80} src={resellerLogoUrl} alt="logo" />
					</div>
				) : (
					<h1> {currentReseller && currentReseller.businessName} </h1>
				)
			) : isMobile ? (
				<img src={MobileLogo} alt="logo" height={80} width={80} />
			) : (
				<img src={DesktopLogo} alt="logo" />
			)}
		</div>
	);

	useEffect(() => {
		const handleResize = () => {
			setIsSmallScreen(window.outerWidth < 768);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const handleMenuSelect = (key: UserDashboardSiderItem) => {
		dispatch(setCurrentPage(key));
		// Reset selected agent when navigating to dashboard
		if (key === UserDashboardSiderItem.DASHBOARD) {
			dispatch(setSelectedAgent({ user: null }));
			dispatch(setSelectedAgentId({ selectedAgentId: null }));
		}
	};

	const { data } = useQuery({
		queryKey: ['getResellerInfoByReceptionist'],
		queryFn: () => getReceptionistReseller({ resellerId: loggedInUser.resellerId || '' }),
		enabled: !!(debrand && loggedInUser.resellerId),
	});

	useEffect(() => {
		if (data) {
			if (currentReseller.emailAddress) {
				return;
			} else {
				dispatch(setReseller(data.reseller));
			}

			if (typeof window !== 'undefined' && data?.reseller?.reseller_name) {
				try {
					const title = debrand ? `${data?.reseller.reseller_name} | Login` : 'Frontdesk | Login';

					// Update the document title (tab title)
					document.title = title;

					// Update the meta description
					const metaDescription = document.querySelector('meta[name="description"]');
					if (metaDescription) {
						metaDescription.setAttribute('content', title);
					}

					// If you still need to update an element with id 'page-title'
					const pageTitle = document.getElementById('page-title');
					if (pageTitle) {
						pageTitle.textContent = title;
					}
				} catch (err) {
					console.error('Error updating page metadata:', err);
				}
			}
		}
	}, [data, debrand]);

	useEffect(() => {
		posthog.capture('user_dashboard_loaded');
	}, [searchParams]);

	// First, let's create a helper function to apply theme
	const applyTheme = (theme: Partial<Theme>) => {
		dispatch(setAppTheme(theme));
	};

	useEffect(() => {
		// Retrieve theme from localStorage if available
		const storedTheme = localStorage.getItem('dashboardTheme');
		try {
			if (storedTheme && storedTheme !== 'undefined') {
				applyTheme(JSON.parse(storedTheme) as Theme);
			}
		} catch (err) {
			console.error('Error parsing dashboardTheme:', err);
		}

		// Update theme based on currentReseller data
		if (currentReseller) {
			if (Object.keys(currentReseller.dashboardTheme || {}).length > 0) {
				// Set theme from currentReseller's dashboardTheme
				applyTheme(currentReseller.dashboardTheme as Theme);
				// Store theme in localStorage
				localStorage.setItem('dashboardTheme', JSON.stringify(currentReseller.dashboardTheme));
			} else if (currentReseller.theme_color) {
				const theme = { buttonSuccessColor: currentReseller.theme_color };
				applyTheme(theme);
				// Store theme in localStorage
				localStorage.setItem('dashboardTheme', JSON.stringify(theme));
			}
		}
	}, [currentReseller]);

	useEffect(() => {
		const initializeReceptionistDashboard = async () => {
			// Skip if we've already successfully initialized
			if (hasInitialized.current) {
				setIsInitializing(false);
				return;
			}

			// If we haven't attempted initialization yet, mark that we're trying
			if (!hasAttemptedInitialization.current) {
				hasAttemptedInitialization.current = true;
			}

			console.log('Starting initialization...', {
				debrand,
				userType: loggedInUser?.userType,
				inboundAgentsLength: inboundAgents.length,
				selectedAgentId,
				currentPage,
			});

			try {
				setIsInitializing(true);
				setInitializationError(null);

				// Fetch agents if not already loaded
				if (inboundAgents.length === 0) {
					console.log('Fetching agents...');
					await refetchAgents();
					return; // Wait for the next effect run when agents are loaded
				}

				// Select first agent if available and no agent is currently selected
				if (
					inboundAgents.length > 0 &&
					inboundAgents.length === 1 &&
					!selectedAgentId &&
					!outboundAgents.length
				) {
					console.log('Selecting first agent...');
					const firstAgent = inboundAgents[0];
					dispatch(setSelectedAgentId({ selectedAgentId: firstAgent.number }));
					dispatch(setSelectedAgent({ user: firstAgent }));
					dispatch(setCurrentPage(ReceptionistDashboardSiderItem.RECEPTIONIST));
					setShouldShowReceptionistDashboard(true);
				} else if (inboundAgents.length === 0) {
					setInitializationError('No agents available');
				}

				console.log('Initialization complete', {
					inboundAgentsLength: inboundAgents.length,
					selectedAgentId,
					currentPage,
				});

				// Mark as initialized only if we've successfully processed the agents
				hasInitialized.current = true;
			} catch (error) {
				console.error('Initialization error:', error);
				setInitializationError(error instanceof Error ? error.message : 'Failed to initialize');
			} finally {
				setIsInitializing(false);
			}
		};

		initializeReceptionistDashboard();
	}, [
		debrand,
		loggedInUser?.userType,
		inboundAgents,
		selectedAgentId,
		currentPage,
		dispatch,
		refetchAgents,
	]);
	const baseClass = 'app-dashboard';

	// Show loading screen during initialization
	if (isInitializing) {
		return <Loading />;
	}

	// Show error if initialization failed
	if (initializationError) {
		return (
			<div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-4">
				<div className="max-w-md w-full bg-white rounded-xl shadow-2xl p-8 space-y-6 transform transition-all duration-300 hover:shadow-3xl">
					<div className="flex flex-col items-center space-y-4">
						<div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center animate-pulse">
							<ErrorOutline className="text-4xl text-red-500" />
						</div>
						<h2 className="text-2xl font-bold text-gray-800 tracking-tight">
							Oops! Something went wrong
						</h2>
						<div className="bg-red-50 border border-red-100 rounded-lg p-4 w-full">
							<p className="text-red-600 text-center font-medium">{initializationError}</p>
						</div>
					</div>

					<div className="flex flex-col space-y-3 pt-4">
						<button
							onClick={() => window.location.reload()}
							className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 font-medium flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
						>
							<Refresh className="text-xl" />
							<span>Try Again</span>
						</button>

						<Button
							onClick={() => {
								LocalStorageUtility.clearLocalData();
								window.location.reload();
							}}
							className="w-full px-4 py-3 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium flex items-center justify-center space-x-2 shadow-sm hover:shadow-md"
						>
							<Logout className="text-xl" />
							<span>Logout</span>
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return shouldShowReceptionistDashboard ? (
		<ReceptionistDashboard debrand={debrand} />
	) : (
		<div className={baseClass}>
			<Layout
				className={`${baseClass}__layout`}
				style={{
					height: '100vh',
				}}
			>
				{isSmallScreen && (
					<div className="flex items-center justify-between bg-white p-4">
						{logo}
						<MenuOutlined onClick={openDrawer} />
					</div>
				)}

				{!isSmallScreen && (
					<Sidebar
						activeMenuItem={currentPage}
						onMenuSelect={handleMenuSelect}
						className={`${baseClass}__menu-icon`}
						menuRef={menuRef}
						debrand={debrand}
					/>
				)}

				{isSmallScreen && drawerVisible && (
					<div
						className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40"
						onClick={handleBackdropClick}
					>
						<div
							className="fixed left-0 top-0 h-full w-64 bg-white shadow-lg z-50"
							onClick={handleSidebarClick}
						>
							<Sidebar
								activeMenuItem={currentPage}
								onMenuSelect={(key) => {
									handleMenuSelect(key);
									closeDrawer();
								}}
								className={`${baseClass}__menu-icon`}
								menuRef={menuRef}
								debrand={debrand}
							/>
						</div>
					</div>
				)}

				<Layout
					style={{
						width: '100%',
						transition: 'none',
						flex: 1,
						overflow: 'auto',
					}}
				>
					<Content>
						<ReceptionistSubCancelPromo />
						<ContentComponent currentPage={currentPage} loggedInUser={loggedInUser} />
					</Content>
				</Layout>
			</Layout>
		</div>
	);
};
